"use client"

import React, { useEffect, useRef, useMemo } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useTableOfContents } from "@/hooks/use-table-of-contents"
import { ContextualToolCTA } from "@/components/blog/contextual-tool-cta"
import { InlineToolWidget } from "@/components/blog/inline-tool-widget"
import { BlogImageLightbox } from "@/components/blog/blog-image-lightbox"
import { AffiliateCardBlock } from "@/components/blog/affiliate-card-block"
import { ProductGuideCta } from "@/components/lead-magnets/product-guide-cta"
import { useBlogTracking } from "@/hooks/useBlogTracking"
import { contentAnalytics, trackEvent } from "@/lib/analytics"
import { parseBlogContent, type ContentPart } from "@/lib/blog-content-parts"
import { hasLedeParagraph } from '@/lib/blog-lede'
import { isAffiliateCardDisabled } from "@/lib/affiliate-card-pilot"
import { countCoveredProducts } from "@/lib/affiliate-card-lookup"
import { resolveAffiliateProductName } from "@/lib/affiliate-product-names"
import { isProductGuideIntent } from "@/lib/blog-intent"
import { AD_GUARDED_TOOLS, splitTrailingBlock } from "@/lib/ad-scanner-guard"
import { IDENTIFY_CTA, identifyCtaProps } from "@/lib/identify-cta"
import { SCAN_CTA_DESTINATION_ATTR } from "@/lib/scan-cta-marker"

const MIN_COVERED_PRODUCTS_FOR_ENHANCED_CARD = 2

// Ad-scanner guard rollout flag. OFF (unset) renders exactly as before; the
// mechanism is unverified against our markup and changing ad placement is a
// time-series boundary, so it ships dark. See lib/ad-scanner-guard.ts and the
// enable conditions in obsidian/flags.md.
const AD_CTA_GUARD = process.env.NEXT_PUBLIC_AD_CTA_GUARD === "true"

interface BlogContentWithTOCProps {
  content: string
  className?: string
  insertInlineCTA?: boolean
  inlineCTAAfterH2?: number // Which H2 to insert after (1-indexed, default 2)
  /** Insert the product-guide lead magnet after the Nth H2. Skipped if the post has fewer H2s. */
  productGuideAfterH2?: number
  /** Article slug for tracking (optional - will extract from pathname if not provided) */
  articleSlug?: string
  /** Category for tracking */
  category?: string
  /** Estimated reading time in minutes */
  readingTimeEstimate?: number
  /** Article tags, forwarded to inline widgets for scoring signals */
  tags?: string[]
}

interface InjectionPlan {
  position: number
  key: string
  component: React.ReactNode
}

/**
 * Render an array of content parts (html chunks interspersed with widgets and
 * the enhanced affiliate block). HTML chunks are wrapped in prose-enhanced so
 * that prose typography styles don't leak into widget/card UI elements.
 */
function RenderParts({
  parts,
  tags,
  slug,
  category,
  isFirstRegion = false,
}: {
  parts: ContentPart[]
  tags?: string[]
  slug: string
  category?: string
  isFirstRegion?: boolean
}) {
  const slugDisabled = isAffiliateCardDisabled(slug)
  // The body is split into multiple .prose-enhanced segments at each injected
  // widget. Tag only the first one so the editorial lede + drop-cap fire once
  // per article, not at the top of every segment.
  // A segment whose only paragraphs sit inside a wrapper (the Fast Answer box)
  // has no direct-child <p> for the lede CSS to style, so prefer the first
  // segment that has one; otherwise a widget injected right after the Fast
  // Answer would steal the drop-cap slot. Fall back to the first html segment
  // so a post with no top-level paragraph keeps its host element (Mediavine's
  // content selector anchors on .prose-lede-host; it must never disappear).
  const ledeIndex = parts.findIndex((p) => p.type === 'html' && hasLedeParagraph(p.html))
  const firstHtmlIndex = ledeIndex !== -1 ? ledeIndex : parts.findIndex((p) => p.type === 'html')

  // originalIndex null = "this fragment is not a whole part" (a peeled tail),
  // so it can never claim the lede drop-cap. A numeric sentinel is unsafe here:
  // findIndex returns -1 when there is no html part, which would make -1 a
  // legitimate firstHtmlIndex value and hand the tail a spurious drop-cap.
  const renderHtml = (html: string, key: React.Key, originalIndex: number | null) => (
    <div
      key={key}
      className={`prose-enhanced reading-focus max-w-none${isFirstRegion && originalIndex !== null && originalIndex === firstHtmlIndex ? ' prose-lede-host' : ''}`}
      suppressHydrationWarning
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )

  const renderWidget = (part: Extract<ContentPart, { type: 'widget' }>, key: React.Key) => (
    <InlineToolWidget
      key={key}
      tool={part.tool}
      variant={part.variant}
      slug={part.slug}
      category={category}
      tags={tags}
      payload={part.payload}
    />
  )

  // Ad-scanner guard: bind a protected widget to the paragraph above it inside
  // one wrapper so Mediavine cannot insert an ad into that gap. See
  // lib/ad-scanner-guard.ts. Only fires for AD_GUARDED_TOOLS, and only when the
  // preceding segment splits cleanly — otherwise everything renders as before.
  const guarded = new Set<number>()
  if (AD_CTA_GUARD) {
    for (let i = 0; i < parts.length - 1; i++) {
      const next = parts[i + 1]
      if (parts[i].type === 'html' && next.type === 'widget' && AD_GUARDED_TOOLS.has(next.tool)) {
        guarded.add(i)
      }
    }
  }

  const nodes: React.ReactNode[] = []
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]

    if (guarded.has(i) && part.type === 'html') {
      const { head, tail } = splitTrailingBlock(part.html)
      if (tail) {
        const widget = parts[i + 1] as Extract<ContentPart, { type: 'widget' }>
        // A whitespace-only head would render an empty .prose-enhanced div —
        // harmless visually, but it is one more sibling for the scanner to
        // consider, which is the opposite of this feature's intent.
        if (head.trim()) nodes.push(renderHtml(head, i, i))
        nodes.push(
          <div key={`guard-${i}`} className="ad-cta-guard">
            {renderHtml(tail, `${i}-tail`, null)}
            {renderWidget(widget, `${i}-widget`)}
          </div>
        )
        i++ // the widget rendered inside the guard
        continue
      }
    }

    nodes.push(renderNode(part, i))
  }

  return <>{nodes}</>

  function renderNode(part: ContentPart, i: number) {
    if (part.type === 'html') {
      return renderHtml(part.html, i, i)
    }
    if (part.type === 'widget') {
      return renderWidget(part, i)
    }
    // Affiliate block: enhanced card by default, with structural guards.
    // Fall back to the original bulleted HTML when:
    //   - the slug is on the kill-switch list
    //   - the parser yielded no products (regex miss or malformed block)
    //   - too few products have enrichment for the card to look right
    const canEnhance =
      !slugDisabled &&
      part.products.length > 0 &&
      countCoveredProducts(part.products) >= MIN_COVERED_PRODUCTS_FOR_ENHANCED_CARD
    if (canEnhance) {
      return <AffiliateCardBlock key={i} products={part.products} slug={slug} />
    }
    return (
      <div
        key={i}
        className="prose-enhanced reading-focus max-w-none"
        suppressHydrationWarning
        dangerouslySetInnerHTML={{ __html: part.fallbackHtml }}
      />
    )
  }
}

export function BlogContentWithTOC({
  content,
  className = "",
  insertInlineCTA = false,
  inlineCTAAfterH2 = 2,
  productGuideAfterH2,
  articleSlug,
  category = "General",
  readingTimeEstimate = 5,
  tags
}: BlogContentWithTOCProps) {
  const router = useRouter()
  const pathname = usePathname()
  const contentRef = useRef<HTMLDivElement>(null)

  // Extract slug from pathname if not provided
  const effectiveSlug = articleSlug || pathname?.split('/').pop() || 'unknown'
  const useProductGuideLeadMagnet = isProductGuideIntent(effectiveSlug, category, tags)

  // Initialize blog tracking
  useBlogTracking({
    articleSlug: effectiveSlug,
    category,
    readingTimeEstimate,
    enabled: true
  })

  const { processedContent: rawProcessedContent } = useTableOfContents({
    content,
    showReadingTimes: false,
    minHeadings: 2
  })

  // Content is already processed with ads inserted in content-processor.ts.
  // Inject .article-lede class on the first <p> so the Speakable schema
  // selector ([".article-lede"]) targets the lede paragraph for AI Overviews
  // / Perplexity citations. Simple first-match replace — assumes Directus
  // WYSIWYG output emits `<p>` without a pre-existing class attribute (true
  // in practice; if not, posts just don't get the speakable marker).
  const processedContent = rawProcessedContent.replace(
    /<p(>|\s)/,
    '<p class="article-lede"$1'
  )

  // Compute split points for ALL inline injections (contextual CTA + newsletter).
  // Each injection inserts a component "after the Nth H2", which means right
  // BEFORE the H2 at index N (zero-indexed). If the post has fewer than N H2s,
  // the injection falls back to the end of the content (parity with the
  // previous single-CTA behavior). If the post has fewer than 1 H2, the
  // injection is skipped entirely.
  const { segments, injections, tails } = useMemo(() => {
    const h2Regex = /<h2[^>]*>/gi
    const h2Positions: number[] = []
    let m: RegExpExecArray | null
    while ((m = h2Regex.exec(processedContent)) !== null) {
      h2Positions.push(m.index)
    }

    function computeSplitPoint(afterH2: number): number | null {
      if (afterH2 <= 0) return null
      if (h2Positions.length < afterH2) return null // not enough headings — skip
      return h2Positions[afterH2] ?? processedContent.length
    }

    const plans: InjectionPlan[] = []

    if (insertInlineCTA) {
      const position = computeSplitPoint(inlineCTAAfterH2)
      if (position !== null) {
        plans.push({
          position,
          key: "cta",
          component: (
            <ContextualToolCTA slug={effectiveSlug} category={category} tags={tags} />
          ),
        })
      }
    }

    // Product-intent posts get the lead-magnet CTA here. Non-product posts
    // used to get the generic InlineNewsletterCTA in this slot, but it
    // measured 0% conversion (0/241 shown, July 2026 PostHog audit) and every
    // post now carries a topic-matched ReminderWidget mid-content (server-side
    // marker via insertReminderWidgetMarkers), so the generic CTA was pure
    // interruption budget spent for nothing — dropped.
    if (productGuideAfterH2 && productGuideAfterH2 > 0 && useProductGuideLeadMagnet) {
      const position = computeSplitPoint(productGuideAfterH2)
      if (position !== null) {
        plans.push({
          position,
          key: "product-guide",
          component: (
            <ProductGuideCta source={`blog_inline_${effectiveSlug}`} variant="inline" />
          ),
        })
      }
    }

    plans.sort((a, b) => a.position - b.position)

    if (plans.length === 0) {
      return { segments: [processedContent], injections: [] as InjectionPlan[], tails: [] as string[] }
    }

    const chunks: string[] = []
    // Parallel to `plans`: the trailing block peeled off the segment before
    // each CTA, rendered inside the guard wrapper with it so Mediavine has no
    // boundary to insert into. Empty string = render the CTA bare, as before.
    const tailChunks: string[] = []
    let prev = 0
    for (const plan of plans) {
      const raw = processedContent.slice(prev, plan.position)
      if (AD_CTA_GUARD) {
        const { head, tail } = splitTrailingBlock(raw)
        chunks.push(head)
        tailChunks.push(tail)
      } else {
        chunks.push(raw)
        tailChunks.push('')
      }
      prev = plan.position
    }
    chunks.push(processedContent.slice(prev))

    return { segments: chunks, injections: plans, tails: tailChunks }
  }, [
    processedContent,
    insertInlineCTA,
    inlineCTAAfterH2,
    productGuideAfterH2,
    effectiveSlug,
    category,
    useProductGuideLeadMagnet,
  ])

  // Second pass: split each segment at widget markers + affiliate blocks.
  const parsedSegments = useMemo(
    () => segments.map((s) => parseBlogContent(s)),
    [segments]
  )
  const parsedTails = useMemo(
    () => tails.map((t) => (t ? parseBlogContent(t) : [])),
    [tails]
  )

  // Handle internal link clicks for client-side navigation
  useEffect(() => {
    const contentElement = contentRef.current
    if (!contentElement) return

    // Older Directus posts sometimes encoded an internal post as "#slug".
    // Normalize those anchors before any interaction so Command-click, middle
    // click, copy-link, and the context menu all see the real destination.
    contentElement.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach((link) => {
      const href = link.getAttribute('href')
      const slug = href?.slice(1) ?? ''
      if (/^[a-z0-9-]+$/.test(slug) && !document.getElementById(slug)) {
        link.setAttribute('href', `/blog/${slug}`)
      }
    })

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement

      // Find the closest anchor tag
      const link = target.closest('a')
      if (!link) return

      const href = link.getAttribute('href')
      if (!href) return

      // The diagnose CTA row at the foot of the fast-answer card. Raw content
      // anchors are otherwise untracked, so this keeps the merged ask
      // measurable alongside the React CTAs' blog_scan_handoff events.
      if (href.startsWith('/diagnose') && link.classList.contains('diagnose-row-cta')) {
        trackEvent('blog_scan_handoff', {
          cta_location: 'fast_answer_merged',
          status: 'clicked',
        })
        return
      }

      // Auto-linked prose CTAs into the app flows (grass-ID, calculators). The
      // anchors live in raw article HTML, so they carry no React handler; the
      // render-time normalizer (lib/internal-links.ts) tags them with
      // data-cta-location and this branch turns that into cta_clicked. Same
      // mechanism as the diagnose-row-cta class above and the data-affiliate
      // anchors below. Navigation is left to the browser.
      const proseCtaLocation = link.getAttribute('data-cta-location')
      if (proseCtaLocation) {
        const toolId = link.getAttribute('data-cta-tool') || undefined
        const ctaText = link.textContent?.trim() || ''
        const isIdentify = href === IDENTIFY_CTA.href
        trackEvent('cta_clicked', {
          ...(isIdentify
            ? identifyCtaProps(proseCtaLocation, ctaText ? { text: ctaText } : undefined)
            : {
                cta_location: proseCtaLocation,
                cta_text: ctaText,
                destination: toolId ?? 'tool',
                destination_url: href,
              }),
          article_slug: effectiveSlug,
          ...(toolId ? { tool_id: toolId } : {}),
          // Whether an in-place picker to the same destination was also on the
          // page. The render pass already drops the prose link where the
          // IN-CONTENT CTA is the uploader, but the post-read/footer scan slots
          // are decided separately, so this stays the honest check and keeps the
          // prose link nettable out of the inline-vs-link read.
          competing_inline_cta:
            isIdentify &&
            document.querySelector(
              `[${SCAN_CTA_DESTINATION_ATTR}="${IDENTIFY_CTA.href}"]`
            ) !== null,
        })
        return
      }

      // Handle internal blog links (starting with /blog/)
      // Exclude anchor-only links (#) and external links (http/https)
      if (href.startsWith('/blog/') && !href.startsWith('http')) {
        // Extract the path (handle cases like /blog/slug#section)
        const path = href.split('#')[0]
        const toSlug = path.replace('/blog/', '')
        const linkText = link.textContent?.trim() || ''

        // Track internal link click
        contentAnalytics.internalLinkClicked(effectiveSlug, toSlug, linkText)

        // Preserve native browser navigation for modified clicks. Calling
        // preventDefault here used to suppress Command-click across every
        // internal link rendered from blog HTML.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return

        e.preventDefault()
        e.stopPropagation()

        // Use Next.js router for client-side navigation
        router.push(path)
        return
      }

      // Handle anchor-only links that might be incorrectly formatted
      // (e.g., #how-to-start-a-lawn-from-scratch should be /blog/how-to-start-a-lawn-from-scratch)
      if (href.startsWith('#') && href.length > 1) {
        const slug = href.substring(1) // Remove the #
        if (document.getElementById(slug)) return
        // Only convert if it looks like a blog post slug (has hyphens, lowercase)
        if (/^[a-z0-9-]+$/.test(slug)) {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return

          e.preventDefault()
          e.stopPropagation()

          const linkText = link.textContent?.trim() || ''

          // Track internal link click
          contentAnalytics.internalLinkClicked(effectiveSlug, slug, linkText)

          router.push(`/blog/${slug}`)
          return
        }
      }

      // Track affiliate link clicks
      if (link.hasAttribute('data-affiliate')) {
        const placementAttr = link.getAttribute('data-affiliate-placement')
        // card-v1 links live inside React cards that fire their own analytics event;
        // skipping here prevents a duplicate capture.
        if (placementAttr === 'card-v1') {
          return
        }
        const productName = resolveAffiliateProductName(link.textContent?.trim() || '', href || '')
        // Cast: placementAttr is validated at write-time by the sanitizer allowlist
        // and the typed analytics union — unknown strings still capture cleanly.
        // Default is 'in-content': links reaching this listener without an explicit
        // placement are auto-linked (lib/affiliate-linker.ts) or hand-authored links
        // living in the prose. React product cards carry data-affiliate-placement
        // and either fire their own event ('card-v1' skip above) or set a real placement.
        const placement = (placementAttr || 'in-content') as Parameters<typeof contentAnalytics.affiliateLinkClicked>[3]
        contentAnalytics.affiliateLinkClicked(effectiveSlug, productName, href || '', placement)
        // Emit the unified click on the SAME surface the impression observer
        // below uses ('blog-inline-anchor') so affiliate_product_shown /
        // affiliate_product_clicked share a join key and per-surface CTR is
        // finally computable for prose links (the impression was added in the
        // 2026-07 audit; the click side was the missing half). Fires ALONGSIDE
        // the legacy affiliate_link_clicked so historical funnels stay intact.
        contentAnalytics.affiliateProductClicked('blog-inline-anchor', {
          productName,
          affiliateLink: href || '',
          article_slug: effectiveSlug,
          placement,
        })
        // Allow default navigation to continue
      }
    }

    contentElement.addEventListener('click', handleClick)

    return () => {
      contentElement.removeEventListener('click', handleClick)
    }
  }, [router, processedContent, effectiveSlug])

  // Impression tracking for plain inline affiliate anchors. The curated React
  // product cards fire affiliate_product_shown via useAffiliateImpression, but
  // auto-linked / hand-authored <a data-affiliate> anchors in the prose only
  // had click tracking — posts monetized purely inline were impression-blind
  // (2026-07 PostHog audit), so per-surface CTR was uncomputable. Mirrors the
  // hook's semantics: 0.5 visibility threshold, once per anchor per pageview.
  useEffect(() => {
    const contentElement = contentRef.current
    if (!contentElement || typeof IntersectionObserver === 'undefined') return

    const anchors = Array.from(
      contentElement.querySelectorAll<HTMLAnchorElement>('a[data-affiliate="true"]')
    ).filter((a) => a.getAttribute('data-affiliate-placement') !== 'card-v1')
    if (anchors.length === 0) return

    const seen = new WeakSet<Element>()
    let remaining = anchors.length
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || seen.has(entry.target)) continue
          seen.add(entry.target)
          observer.unobserve(entry.target)
          remaining--
          const link = entry.target as HTMLAnchorElement
          const href = link.getAttribute('href') || ''
          contentAnalytics.affiliateProductShown('blog-inline-anchor', {
            article_slug: effectiveSlug,
            product_name: resolveAffiliateProductName(link.textContent?.trim() || '', href),
            affiliate_link: href,
            asin: href.match(/\/dp\/([A-Z0-9]{10})/)?.[1],
            placement: link.getAttribute('data-affiliate-placement') || 'in-content',
          })
          if (remaining === 0) observer.disconnect()
        }
      },
      { threshold: 0.5 }
    )
    anchors.forEach((a) => observer.observe(a))

    return () => observer.disconnect()
  }, [processedContent, effectiveSlug])


  return (
    <div className={`relative ${className}`} ref={contentRef}>
      <RenderParts parts={parsedSegments[0]} tags={tags} slug={effectiveSlug} category={category} isFirstRegion />
      {injections.map((plan, i) => (
        <React.Fragment key={plan.key}>
          {/* Guard on: the trailing paragraph of the segment above renders in
              the same wrapper as the CTA, so there is no sibling boundary for
              Mediavine to insert into. Guard off (or no clean split): the CTA
              renders bare, exactly as before. */}
          {parsedTails[i]?.length ? (
            <div className="ad-cta-guard">
              <RenderParts
                parts={parsedTails[i]}
                tags={tags}
                slug={effectiveSlug}
                category={category}
              />
              {plan.component}
            </div>
          ) : (
            plan.component
          )}
          <RenderParts
            parts={parsedSegments[i + 1]}
            tags={tags}
            slug={effectiveSlug}
            category={category}
          />
        </React.Fragment>
      ))}
      <BlogImageLightbox containerRef={contentRef} contentKey={effectiveSlug} />
    </div>
  )
}
